export type AgentFontCategory =
  | 'sans-serif'
  | 'serif'
  | 'display'
  | 'handwriting'
  | 'monospace';

export type AgentFontDefinition = {
  id: string;
  family: string;
  category: AgentFontCategory;
};

const FONT_FALLBACKS: Record<AgentFontCategory, string> = {
  'sans-serif': 'sans-serif',
  serif: 'serif',
  display: 'sans-serif',
  handwriting: 'cursive',
  monospace: 'monospace',
};

export const DEFAULT_AGENT_FONT_ID = 'open-sans';

export const AGENT_FONT_CATALOG: AgentFontDefinition[] = [
  { id: 'open-sans', family: 'Open Sans', category: 'sans-serif' },
  { id: 'montserrat', family: 'Montserrat', category: 'sans-serif' },
  { id: 'poppins', family: 'Poppins', category: 'sans-serif' },
  { id: 'roboto', family: 'Roboto', category: 'sans-serif' },
  { id: 'lato', family: 'Lato', category: 'sans-serif' },
  { id: 'nunito-sans', family: 'Nunito Sans', category: 'sans-serif' },
  { id: 'inter', family: 'Inter', category: 'sans-serif' },
  { id: 'manrope', family: 'Manrope', category: 'sans-serif' },
  { id: 'dm-sans', family: 'DM Sans', category: 'sans-serif' },
  { id: 'work-sans', family: 'Work Sans', category: 'sans-serif' },
  { id: 'figtree', family: 'Figtree', category: 'sans-serif' },
  { id: 'plus-jakarta-sans', family: 'Plus Jakarta Sans', category: 'sans-serif' },
  { id: 'outfit', family: 'Outfit', category: 'sans-serif' },
  { id: 'space-grotesk', family: 'Space Grotesk', category: 'sans-serif' },
  { id: 'source-sans-3', family: 'Source Sans 3', category: 'sans-serif' },
  { id: 'mulish', family: 'Mulish', category: 'sans-serif' },
  { id: 'urbanist', family: 'Urbanist', category: 'sans-serif' },
  { id: 'barlow', family: 'Barlow', category: 'sans-serif' },
  { id: 'ibm-plex-sans', family: 'IBM Plex Sans', category: 'sans-serif' },
  { id: 'pt-sans', family: 'PT Sans', category: 'sans-serif' },
  { id: 'karla', family: 'Karla', category: 'sans-serif' },
  { id: 'rubik', family: 'Rubik', category: 'sans-serif' },
  { id: 'archivo', family: 'Archivo', category: 'sans-serif' },
  { id: 'quicksand', family: 'Quicksand', category: 'sans-serif' },
  { id: 'merriweather', family: 'Merriweather', category: 'serif' },
  { id: 'lora', family: 'Lora', category: 'serif' },
  { id: 'playfair-display', family: 'Playfair Display', category: 'serif' },
  { id: 'libre-baskerville', family: 'Libre Baskerville', category: 'serif' },
  { id: 'cormorant-garamond', family: 'Cormorant Garamond', category: 'serif' },
  { id: 'crimson-text', family: 'Crimson Text', category: 'serif' },
  { id: 'eb-garamond', family: 'EB Garamond', category: 'serif' },
  { id: 'alegreya', family: 'Alegreya', category: 'serif' },
  { id: 'bitter', family: 'Bitter', category: 'serif' },
  { id: 'arvo', family: 'Arvo', category: 'serif' },
  { id: 'source-serif-4', family: 'Source Serif 4', category: 'serif' },
  { id: 'dm-serif-display', family: 'DM Serif Display', category: 'serif' },
  { id: 'fraunces', family: 'Fraunces', category: 'serif' },
  { id: 'raleway', family: 'Raleway', category: 'display' },
  { id: 'oswald', family: 'Oswald', category: 'display' },
  { id: 'bebas-neue', family: 'Bebas Neue', category: 'display' },
  { id: 'anton', family: 'Anton', category: 'display' },
  { id: 'abril-fatface', family: 'Abril Fatface', category: 'display' },
  { id: 'comfortaa', family: 'Comfortaa', category: 'display' },
  { id: 'caveat', family: 'Caveat', category: 'handwriting' },
  { id: 'pacifico', family: 'Pacifico', category: 'handwriting' },
  { id: 'space-mono', family: 'Space Mono', category: 'monospace' },
  { id: 'ibm-plex-mono', family: 'IBM Plex Mono', category: 'monospace' },
  { id: 'roboto-mono', family: 'Roboto Mono', category: 'monospace' },
  { id: 'inconsolata', family: 'Inconsolata', category: 'monospace' },
  { id: 'jetbrains-mono', family: 'JetBrains Mono', category: 'monospace' },
];

const AGENT_FONT_MAP = new Map(
  AGENT_FONT_CATALOG.map((font) => [font.id, font]),
);

function slugifyFontFamily(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function normalizeAgentFontId(value?: string | null): string {
  if (!value || typeof value !== 'string') {
    return DEFAULT_AGENT_FONT_ID;
  }

  const normalized = slugifyFontFamily(value);
  return normalized || DEFAULT_AGENT_FONT_ID;
}

export function resolveAgentFont(value?: string | null): AgentFontDefinition {
  const normalized = normalizeAgentFontId(value);
  return (
    AGENT_FONT_MAP.get(normalized) ??
    AGENT_FONT_MAP.get(DEFAULT_AGENT_FONT_ID)!
  );
}

export function getAgentFontCssFamily(value?: string | null): string {
  const font = resolveAgentFont(value);
  return `'${font.family}', ${FONT_FALLBACKS[font.category]}`;
}

export function getAgentFontStylesheetUrl(value?: string | null): string {
  const font = resolveAgentFont(value);
  const encodedFamily = encodeURIComponent(font.family).replace(/%20/g, '+');
  return `https://fonts.googleapis.com/css2?family=${encodedFamily}&display=swap`;
}
